/**
 * "Equipment on location" — every asset not in the yard, on the dispatch board.
 *
 * THE RULE (operator, standing): dispatch always sees ALL equipment, with its
 * location and status, whatever state its job is in. The board used to show
 * gear only as a name inside a job card, so gear left on a COMPLETED job outside
 * the week on screen was not on the board at all (TNT's TN 2400, J-4348).
 *
 * Reads GET /dispatch/equipment-out: left_on_site, in_use and reserved,
 * company-wide, no date window. Each row: serial · name · status · rig/well ·
 * job · date. The DATE is the API's — `since_label` is resolved in the
 * tenant's zone server-side and rendered as given; `since_date` is only a sort
 * key here. Nothing about either is computed in the browser.
 *
 * Oldest first by default so stale gear surfaces; an unknown date sorts first,
 * because an unknown age is not a small age.
 */
import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { ArrowDownUp, ChevronDown, ChevronRight, MapPin } from 'lucide-react';
import { api } from '../../api/client';
import { LoadFailureNotice } from '../ui/LoadFailureNotice';
import { LOAD_FAILURE_QUERY_OPTIONS, queryLoadFailure } from '../../lib/load-failure';
import { EQUIPMENT_STATUS_LABELS } from '../../lib/equipment-status';
import { equipmentLabel } from '../../lib/equipment-label';
import type { EquipmentOutRow, EquipmentOutStatus } from '../../types/dispatch';

export const EQUIPMENT_OUT_QUERY_KEY = ['dispatch-equipment-out'] as const;

type Filter = 'all' | EquipmentOutStatus;
const FILTERS: Filter[] = ['all', 'left_on_site', 'in_use', 'reserved'];
const FILTER_LABELS: Record<Filter, string> = {
  all: 'All',
  left_on_site: EQUIPMENT_STATUS_LABELS.left_on_site,
  in_use: EQUIPMENT_STATUS_LABELS.in_use,
  reserved: EQUIPMENT_STATUS_LABELS.reserved,
};

const STATUS_FILL: Record<EquipmentOutStatus, string> = {
  left_on_site: 'bg-fi-signal-amber',
  in_use: 'bg-fi-signal-blue',
  reserved: 'bg-fi-signal-blue',
};

const SINCE_PREFIX: Record<EquipmentOutRow['since_kind'], string> = {
  reserved_for: 'Reserved for',
  in_use_since: 'In use since',
  left_since: 'Left',
};

type Order = 'oldest' | 'newest';

/** Oldest-first on the API's day string; unknown first in both directions. */
export function sortEquipmentOut(rows: EquipmentOutRow[], order: Order): EquipmentOutRow[] {
  const dir = order === 'oldest' ? 1 : -1;
  return [...rows].sort((a, b) => {
    if (a.since_date === b.since_date) {
      return equipmentLabel(a.serial_number, a.name).localeCompare(equipmentLabel(b.serial_number, b.name));
    }
    if (a.since_date === null) return -1;
    if (b.since_date === null) return 1;
    return (a.since_date < b.since_date ? -1 : 1) * dir;
  });
}

function location(row: EquipmentOutRow): string {
  const parts = [row.rig, row.well].filter((p): p is string => !!p && p.trim() !== '');
  return parts.length ? parts.join(' · ') : '—';
}

export default function EquipmentOutStrip() {
  const navigate = useNavigate();
  const [filter, setFilter] = useState<Filter>('all');
  const [order, setOrder] = useState<Order>('oldest');
  const [open, setOpen] = useState(true);

  const query = useQuery({
    queryKey: EQUIPMENT_OUT_QUERY_KEY,
    queryFn: () => api.dispatch.equipmentOut().then((r) => r.data),
    ...LOAD_FAILURE_QUERY_OPTIONS,
  });
  const failure = query.data === undefined ? queryLoadFailure(query) : null;
  const rows = useMemo(() => query.data?.data ?? [], [query.data]);
  const counts = query.data?.counts;

  const visible = useMemo(
    () => sortEquipmentOut(filter === 'all' ? rows : rows.filter((r) => r.status === filter), order),
    [rows, filter, order],
  );

  const tally = (f: Filter): string => {
    if (!counts) return '—';
    return String(f === 'all' ? rows.length : counts[f] ?? 0);
  };

  return (
    <section
      aria-label="Equipment on location"
      className="rounded-fi-panel border border-fieldiq-border bg-fieldiq-card"
    >
      <div className="flex flex-wrap items-center justify-between gap-3 border-b border-fieldiq-border px-4 py-3">
        <button
          type="button"
          onClick={() => setOpen((v) => !v)}
          aria-expanded={open}
          className="flex items-center gap-2 text-sm font-bold text-fieldiq-text"
        >
          {open ? <ChevronDown className="size-4" aria-hidden="true" /> : <ChevronRight className="size-4" aria-hidden="true" />}
          <MapPin className="size-4 text-fieldiq-accent" aria-hidden="true" />
          Equipment on location
          <span className="tabular-nums text-fieldiq-muted">{tally('all')}</span>
        </button>
        {open && (
          <div className="flex flex-wrap items-center gap-2">
            {FILTERS.map((f) => {
              const active = filter === f;
              return (
                <button
                  key={f}
                  type="button"
                  aria-pressed={active}
                  onClick={() => setFilter(f)}
                  className={`inline-flex items-center gap-1.5 whitespace-nowrap rounded-fi-pill px-3 py-1 text-xs font-extrabold transition ${
                    active
                      ? 'bg-fi-amber text-fi-amber-ink'
                      : 'border border-fieldiq-border bg-fieldiq-card text-fieldiq-muted hover:text-fieldiq-text'
                  }`}
                >
                  {FILTER_LABELS[f]}
                  <span className="tabular-nums">{tally(f)}</span>
                </button>
              );
            })}
            <button
              type="button"
              onClick={() => setOrder((o) => (o === 'oldest' ? 'newest' : 'oldest'))}
              className="inline-flex items-center gap-1.5 rounded-fi-pill border border-fieldiq-border px-3 py-1 text-xs font-bold text-fieldiq-muted hover:text-fieldiq-text"
            >
              <ArrowDownUp className="size-3.5" aria-hidden="true" />
              {order === 'oldest' ? 'Oldest first' : 'Newest first'}
            </button>
          </div>
        )}
      </div>

      {open && (
        failure ? (
          <LoadFailureNotice error={null} kind={failure} subject="equipment on location" className="py-6" />
        ) : query.data === undefined ? (
          <p className="px-4 py-6 text-sm text-fieldiq-muted">Loading equipment…</p>
        ) : visible.length === 0 ? (
          <p className="px-4 py-6 text-sm text-fieldiq-muted">
            {filter === 'all' ? 'All equipment is in the yard.' : `No equipment ${FILTER_LABELS[filter].toLowerCase()}.`}
          </p>
        ) : (
          <div className="max-h-80 overflow-auto">
            <table className="w-full min-w-[760px] text-left text-sm">
              <thead className="sticky top-0 bg-fieldiq-card text-xs uppercase tracking-wide text-fieldiq-muted">
                <tr>
                  <th className="px-4 py-2 font-semibold">Equipment</th>
                  <th className="px-4 py-2 font-semibold">Status</th>
                  <th className="px-4 py-2 font-semibold">Rig / Well</th>
                  <th className="px-4 py-2 font-semibold">Job</th>
                  <th className="px-4 py-2 font-semibold">Date</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-fieldiq-border">
                {visible.map((row) => (
                  <tr
                    key={row.id}
                    onClick={() => navigate(`/equipment/${row.id}`)}
                    onKeyDown={(e) => { if (e.key === 'Enter') navigate(`/equipment/${row.id}`); }}
                    tabIndex={0}
                    role="link"
                    aria-label={`Open ${equipmentLabel(row.serial_number, row.name)}`}
                    className="cursor-pointer hover:bg-fieldiq-border/30 focus:bg-fieldiq-border/30 focus:outline-none"
                  >
                    <td className="px-4 py-2">
                      <span className="font-bold text-fieldiq-text">{row.serial_number ?? row.name ?? '—'}</span>
                      {row.serial_number && row.name && row.name !== row.serial_number && (
                        <span className="ml-2 text-fieldiq-muted">{row.name}</span>
                      )}
                    </td>
                    <td className="px-4 py-2">
                      <span className={`inline-flex items-center whitespace-nowrap rounded-fi-pill border border-black/20 px-2.5 py-1 text-[0.68rem] font-black uppercase tracking-[0.06em] leading-none text-fi-signal-ink ${STATUS_FILL[row.status] ?? 'bg-fi-signal-slate'}`}>
                        {EQUIPMENT_STATUS_LABELS[row.status] ?? row.status}
                      </span>
                    </td>
                    <td className="px-4 py-2 text-fieldiq-text">{location(row)}</td>
                    <td className="px-4 py-2 text-fieldiq-text">{row.job_number ?? '—'}</td>
                    <td className="whitespace-nowrap px-4 py-2 text-fieldiq-text">
                      {row.since_label
                        ? <><span className="text-fieldiq-muted">{SINCE_PREFIX[row.since_kind]} </span>{row.since_label}</>
                        : '—'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )
      )}
    </section>
  );
}
